import { useEffect, useRef, useState } from 'react';
import { Download, FileSearch } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  ACCOUNTING_ROLES,
  type ConsignmentAccountingMapping,
  type ConsignmentAccountingPreview,
  type ConsignmentStatement,
} from '@tcvs/shared';
import { useConsignmentApi } from './consignment-api';
import { consignmentErrorMessage, formatMoneyMinor as money } from './consignment-ui';

export function ConsignmentAccounting({
  statement,
}: {
  statement: ConsignmentStatement;
}): JSX.Element {
  const api = useConsignmentApi();
  const [mapping, setMapping] = useState<ConsignmentAccountingMapping>({
    company: '',
    vendor: '',
    accounts: {},
  });
  const [report, setReport] = useState<ConsignmentAccountingPreview>();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const generation = useRef(0);
  const pending = useRef(false);
  const identity = `${statement.consignorId}/${statement.statementId}/${statement.financialSummary?.version ?? 0}`;
  useEffect(() => {
    generation.current++;
    setReport(undefined);
    setError(undefined);
    return () => {
      generation.current++;
    };
  }, [identity]);
  const edit = (next: ConsignmentAccountingMapping) => {
    generation.current++;
    setMapping(next);
    setReport(undefined);
    setError(undefined);
  };
  async function generate() {
    if (pending.current) return;
    pending.current = true;
    const current = ++generation.current;
    setBusy(true);
    setError(undefined);
    setReport(undefined);
    try {
      const result = await api.mutate<ConsignmentAccountingPreview>({
        operation: 'PREVIEW_STATEMENT_ACCOUNTING',
        consignorId: statement.consignorId,
        statementId: statement.statementId,
        mapping,
      });
      if (current !== generation.current) return;
      if (!result.ok) throw new Error(consignmentErrorMessage(result));
      if (
        result.data.statementId !== statement.statementId ||
        result.data.consignorId !== statement.consignorId ||
        result.data.mode !== 'REVIEW_ONLY'
      )
        throw new Error('Preview did not match the selected statement.');
      setReport(result.data);
    } catch (e) {
      if (current === generation.current)
        setError(e instanceof Error ? e.message : 'Preview unavailable.');
    } finally {
      pending.current = false;
      setBusy(false);
    }
  }
  function download() {
    if (!report) return;
    const url = URL.createObjectURL(
      new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' }),
    );
    const a = document.createElement('a');
    a.href = url;
    a.download = `accounting-review-${report.statementId}-${report.reportFingerprint.slice(0, 12)}.json`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
  return (
    <section className="tcvs-accounting" aria-label="Accounting preview">
      <h4>Accounting preview</h4>
      <p className="tcvs-accounting-status">
        Review only · Mapping unverified · Export history not checked
      </p>
      <details>
        <summary>Proposed account mappings</summary>
        <div className="tcvs-accounting-mappings">
          <label>
            Business Central company
            <input
              value={mapping.company}
              maxLength={100}
              onChange={(e) => edit({ ...mapping, company: e.target.value })}
            />
          </label>
          <label>
            Consignor vendor number
            <input
              value={mapping.vendor}
              maxLength={20}
              onChange={(e) => edit({ ...mapping, vendor: e.target.value })}
            />
          </label>
          {Object.entries(ACCOUNTING_ROLES).map(([role, label]) => (
            <label key={role}>
              {label}
              <input
                value={mapping.accounts[role as keyof typeof ACCOUNTING_ROLES] ?? ''}
                maxLength={20}
                onChange={(e) =>
                  edit({ ...mapping, accounts: { ...mapping.accounts, [role]: e.target.value } })
                }
              />
            </label>
          ))}
        </div>
      </details>
      <div className="tcvs-statement-toolbar">
        <button disabled={busy || statement.status !== 'APPROVED'} onClick={() => void generate()}>
          <FileSearch size={16} aria-hidden="true" /> {busy ? 'Generating...' : 'Generate preview'}
        </button>
        <button
          className="tcvs-accounting-download"
          disabled={!report || busy}
          onClick={download}
          aria-label="Download accounting review JSON"
          title="Download accounting review JSON"
        >
          <Download size={18} aria-hidden="true" />
        </button>
      </div>
      {error && (
        <p role="alert" className="tcvs-error">
          {error}
        </p>
      )}
      {report && (
        <>
          <dl className="tcvs-detail-list">
            <div>
              <dt>Debits</dt>
              <dd>{money(report.totals.debitMinor)}</dd>
            </div>
            <div>
              <dt>Credits</dt>
              <dd>{money(report.totals.creditMinor)}</dd>
            </div>
            <div>
              <dt>Balance check</dt>
              <dd>{report.totals.balanced ? 'Balanced' : 'Unbalanced'}</dd>
            </div>
            <div>
              <dt>Consignor balance</dt>
              <dd>
                {money(report.finance.balanceAmountMinor)}
                {report.finance.balanceAmountMinor < 0 ? ' (recovery due)' : ''}
              </dd>
            </div>
          </dl>
          {report.issues.length > 0 && (
            <div role="status">
              <h5>Review items</h5>
              <ul>
                {report.issues.map((issue, i) => (
                  <li key={i}>{issue}</li>
                ))}
              </ul>
            </div>
          )}
          <div className="tcvs-table-wrap">
            <table className="tcvs-table tcvs-accounting-table">
              <thead>
                <tr>
                  <th>Source / date</th>
                  <th>Account role</th>
                  <th>Proposed account</th>
                  <th>Debit</th>
                  <th>Credit</th>
                </tr>
              </thead>
              <tbody>
                {report.sources.map((s) =>
                  s.entries.length ? (
                    s.entries.map((e, i) => (
                      <tr key={`${s.sourceId}/${e.role}`}>
                        <td>
                          {i === 0 && (
                            <>
                              <strong>{s.kind.replaceAll('_', ' ')}</strong>
                              <br />
                              {s.date} · v{s.version}
                              <br />
                              <span className="tcvs-accounting-source">{s.sourceId}</span>
                              {s.paymentReference && (
                                <>
                                  <br />
                                  {s.paymentReference}
                                </>
                              )}
                            </>
                          )}
                        </td>
                        <td>{ACCOUNTING_ROLES[e.role]}</td>
                        <td>{e.account ?? 'Unmapped'}</td>
                        <td>{money(e.debitMinor)}</td>
                        <td>{money(e.creditMinor)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr key={s.sourceId}>
                      <td>
                        {s.kind.replaceAll('_', ' ')} · v{s.version}
                        <br />
                        {s.date}
                        <br />
                        <span className="tcvs-accounting-source">{s.sourceId}</span>
                      </td>
                      <td colSpan={4}>No monetary change</td>
                    </tr>
                  ),
                )}
              </tbody>
            </table>
          </div>
          <details>
            <summary>Source cards ({report.cards.length})</summary>
            <ul>
              {report.cards.map((c) => (
                <li key={c.allocationId}>
                  <Link
                    to={`/catalog/${encodeURIComponent(c.cardId)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {c.title ?? c.cardId}
                  </Link>{' '}
                  · {c.orderId} / {c.lineItemId}
                </li>
              ))}
            </ul>
          </details>
          <details>
            <summary>Review limitations</summary>
            <ul>
              {report.limitations.map((l) => (
                <li key={l}>{l}</li>
              ))}
            </ul>
          </details>
          <p className="tcvs-accounting-evidence">
            Generated {report.generatedAt} · Finance v{report.finance.version}
            <br />
            Source {report.sourceFingerprint}
            <br />
            Report {report.reportFingerprint}
          </p>
        </>
      )}
    </section>
  );
}
